import { PaperClipIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useState } from 'react';
import { QUOTE_TYPES, STOCK_CHECK_STATUSES } from '../../pages/WMS_SMS/data';
import { hasValidInventoryPriceForType } from '../../pages/WMS_SMS/functions';

export default function InventoryLineRow({
    inventoryLineData,
    selectedInventory,
    updateSelectedInventory,
    setData,
    setStockCheckData,
    setPickedQuantity,
    setCommentData,
    setNotification,
    ccp = [],
    hasPortalTypeSelected,
}) {
    const [showPopup, setShowPopup] = useState(false);
    const [showTypePopup, setShowTypePopup] = useState(false);
    const [showPriceTypeModal, setShowPriceTypeModal] = useState(false);
    const defaultPopupPosition = { left: '0px', top: '0px' };
    const [popupPositionStyle, setPopupPositionStyle] = useState(defaultPopupPosition);
    const [selectedTrace, setSelectedTrace] = useState([]);

    const TraceTypes = {
        TracePictures: 'Trace Pictures',
        TracePartRecord: 'Trace Part Record',
        TraceTag: 'Trace Tag',
        TraceOwnerCode: 'Trace Owner Code',
        TraceOneClick: 'Trace One Click',
        TraceHeroPhotos: 'Trace Hero Photos',
    };

    const ExchangeTypes = {
        costPlus: 'Cost Plus',
        flatRateOHCOrCore: 'Flat Rate w/OH Core',
        flatRateSVCore: 'Flat Rate w/SV Core',
        flatRateRepairableCore: 'Flat Rate w/Repairable Core',
    };

    const CoreDispositionTypes = {
        overhaul: 'Overhaul',
        serviceable: 'Serviceable',
        // Salesforce restricted picklist uses curly quotes (U+2018 / U+2019), not ASCII apostrophes
        asIs: 'Receive \u2018As Is\u2019',
    };

    const StockDispositionTypes = {
        returnToStock: 'Return to Stock',
        returnToExchangeVendor: 'Return to Exchange Vendor',
    };

    const disableSelectedCheckbox = () => !hasValidInventoryPriceForType(inventoryLineData);

    const setDefaultInventoryValues = () => {
        const defaultTrace = [];
        if (inventoryLineData.pictures) {
            defaultTrace.push({ name: TraceTypes.TracePictures, link: inventoryLineData.pictures });
        }
        if (inventoryLineData.partRecord) {
            defaultTrace.push({ name: TraceTypes.TracePartRecord, link: inventoryLineData.partRecord });
        }
        if (inventoryLineData.tagsLink) {
            defaultTrace.push({ name: TraceTypes.TraceTag, link: inventoryLineData.tagsLink });
        }
        if (inventoryLineData.owner) {
            defaultTrace.push({ name: TraceTypes.TraceOwnerCode, link: inventoryLineData.owner });
        }
        if (inventoryLineData.oneClick && inventoryLineData.oneClick !== 'No Binder Available') {
            defaultTrace.push({ name: TraceTypes.TraceOneClick, link: inventoryLineData.oneClick });
        }
        if (inventoryLineData.photos) {
            const heroPhotos = inventoryLineData?.photos?.filter((photo) => photo.isHero);
            defaultTrace.push({ name: TraceTypes.TraceHeroPhotos, link: heroPhotos });
        }

        setSelectedTrace(defaultTrace);
        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    return {
                        ...data,
                        traceLinks: defaultTrace,
                    };
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });
    };

    const updatePersistedTraceSelection = (e, traceData) => {
        const checked = e.target.checked;

        let updatedSelectedTrace = [];
        if (checked) {
            if (traceData.name === 'Trace Hero Photos' && Array.isArray(traceData.link)) {
                updatedSelectedTrace = [
                    ...selectedTrace.filter((data) => data.name !== 'Trace Hero Photos'),
                    {
                        ...traceData,
                        link: traceData.link.filter((photo) => photo.isHero),
                    },
                ];
            } else {
                updatedSelectedTrace = [...selectedTrace, traceData];
            }
            setSelectedTrace(updatedSelectedTrace);
        }
        if (!checked) {
            updatedSelectedTrace = selectedTrace.filter((data) => data.name !== traceData.name);
            setSelectedTrace(updatedSelectedTrace);
        }

        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    return {
                        ...data,
                        traceLinks: updatedSelectedTrace,
                    };
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });
    };

    const updateInventoryOutrightPrice = (input) => {
        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    return {
                        ...data,
                        outrightPrice: input,
                    };
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });

        // Remove empty price selections
        if ((selectedInventory?.some((inv) => inv === inventoryLineData.inventoryName) && input === 0) || input === '') {
            updateSelectedInventory(
                { target: { checked: false } },
                inventoryLineData.inventoryName,
                inventoryLineData.quantity
            );
        }
    };

    const updateInventoryKeyValue = (input, key) => {
        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    return {
                        ...data,
                        [key]: input,
                    };
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });
    };

    const updateInventoryQuantity = (input) => {
        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    return {
                        ...data,
                        quantity: input,
                    };
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });

        const isSelected = selectedInventory?.some((inv) => inv === inventoryLineData.inventoryName);
        if (isSelected) {
            if (input === '' || input === 0) {
                updateSelectedInventory({ target: { checked: false } }, inventoryLineData.inventoryName, input);
            } else {
                setPickedQuantity((prevQuantity) => prevQuantity - inventoryLineData.quantity + input);
            }
        }
    };

    const updateInventoryExchangeType = (input) => {
        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    let exchangeData;
                    if (input === QUOTE_TYPES.EXCHANGE) {
                        const matchingCCPExchange = (ccp || []).find(
                            (ccpData) =>
                                ccpData.conditionRating === data.conditionRating &&
                                data.partId === ccpData.productId &&
                                ccpData.saleType === QUOTE_TYPES.EXCHANGE
                        );
                        return {
                            ...data,
                            type: input,
                            exchangeType: data.exchangeType || ExchangeTypes.costPlus,
                            coreDue: data.coreDue || 30,
                            coreDisposition: data.coreDisposition || CoreDispositionTypes.asIs,
                            stockDisposition: data.stockDisposition || StockDispositionTypes.returnToStock,
                            exchangePrice: matchingCCPExchange?.price ?? data.exchangePrice ?? null,
                        };
                    }

                    if (input !== QUOTE_TYPES.EXCHANGE) {
                        return {
                            ...data,
                            type: input,
                            exchangePrice: null,
                            exchangeType: null,
                            coreDue: null,
                            coreDisposition: null,
                            stockDisposition: null,
                        };
                    }
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });
    };

    const updateInventoryExchangeData = (input, key) => {
        setData((prevData) => {
            const updatedInventory = prevData?.inventoryLines?.map((data) => {
                if (data.inventoryName === inventoryLineData?.inventoryName) {
                    return {
                        ...data,
                        [key]: input,
                    };
                }
                return data;
            });
            return {
                ...prevData,
                inventoryLines: updatedInventory,
            };
        });
    };

    const checkIfRequiredFieldsAreFilled = () => {
        if (inventoryLineData.type === 'Exchange') {
            if (!inventoryLineData.exchangePrice || inventoryLineData.exchangePrice == 0) {
                return false;
            }
            if (!inventoryLineData.exchangeType) {
                return false;
            }
            if (!inventoryLineData.coreDue) {
                return false;
            }
            if (!inventoryLineData.coreDisposition) {
                return false;
            }
            if (!inventoryLineData.stockDisposition) {
                return false;
            }
        }
        return true;
    };

    const closeModal = () => {
        const requiredFieldsAreFilled = checkIfRequiredFieldsAreFilled();
        if (!requiredFieldsAreFilled) {
            setNotification({
                shown: true,
                status: 'error',
                title: 'Required Fields Not Filled',
                message: 'All required fields must be filled out if the type is Exchange.',
            });
        } else {
            setShowPriceTypeModal(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    useEffect(() => {
        if (hasPortalTypeSelected && inventoryLineData.type === QUOTE_TYPES.EXCHANGE) {
            updateInventoryExchangeType(QUOTE_TYPES.OUTRIGHT);
        }
    }, [hasPortalTypeSelected]);

    useEffect(() => {
        setDefaultInventoryValues();
    }, []);

    return (
        <>
            {showPriceTypeModal && (
                <div
                    className='fixed inset-0 z-30 flex items-center justify-center bg-black/40'
                    onClick={handleOverlayClick}
                >
                    <div
                        className='relative bg-white rounded-md shadow-xl min-w-[700px] min-h-[400px] flex flex-col'
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: 900, width: '90vw', maxHeight: 600 }}
                    >
                        <button
                            className='absolute z-20 flex items-center justify-center w-8 h-8 border top-2 left-2'
                            onClick={() => closeModal()}
                        >
                            <span className='text-xl'>✕</span>
                        </button>
                        {inventoryLineData.type === 'Exchange' ? (
                            <h1 className='text-red-500 text-center absolute bottom-5 left-5'>
                                <span className='text-red-500'>*</span> Values must be filled out if the type is Exchange.
                            </h1>
                        ) : null}
                        <section className='flex flex-row justify-center items-start p-10 gap-5'>
                            <article className='flex flex-col items-center justify-start w-1/2 h-full gap-5'>
                                <div className='flex flex-col justify-center items-start w-full'>
                                    <h1 className='text-md font-bold mb-2 w-fit'>Quote Type</h1>
                                    <select
                                        value={inventoryLineData.type}
                                        className='w-full p-3 rounded-md border bg-white'
                                        onChange={(e) => updateInventoryExchangeType(e.target.value)}
                                    >
                                        {Object.values(QUOTE_TYPES).map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='flex flex-col justify-center items-start w-full'>
                                    <RequiredFieldText text='Exchange Price' inventoryLineData={inventoryLineData} />
                                    <input
                                        type='number'
                                        value={inventoryLineData?.exchangePrice ?? ''}
                                        className='w-full p-3 rounded-md border bg-white disabled:bg-gray-100 disabled:cursor-not-allowed'
                                        disabled={inventoryLineData.type === 'Outright'}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            // Allow digits and at most one decimal point
                                            if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                                                updateInventoryExchangeData(raw === '' ? null : raw, 'exchangePrice');
                                            }
                                        }}
                                    />
                                </div>
                                <div className='flex flex-col justify-center items-start w-full'>
                                    <RequiredFieldText text='Exchange Type' inventoryLineData={inventoryLineData} />
                                    <select
                                        value={inventoryLineData?.exchangeType ?? ''}
                                        className='w-full p-3 rounded-md border bg-white'
                                        onChange={(e) => updateInventoryExchangeData(e.target.value || null, 'exchangeType')}
                                    >
                                        <option value=''>--Select--</option>
                                        {Object.values(ExchangeTypes).map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </article>
                            <article className='flex flex-col items-center justify-start w-1/2 h-full gap-5'>
                                <div className='flex flex-col justify-center items-start w-full'>
                                    <RequiredFieldText text='Core Due (In Days)' inventoryLineData={inventoryLineData} />
                                    <input
                                        type='text'
                                        value={inventoryLineData?.coreDue ?? ''}
                                        onChange={(e) => updateInventoryExchangeData(e.target.value, 'coreDue')}
                                    />
                                </div>
                                <div className='flex flex-col justify-center items-start w-full'>
                                    <RequiredFieldText text='Core Disposition' inventoryLineData={inventoryLineData} />
                                    <select
                                        className='w-full p-3 rounded-md border bg-white'
                                        value={inventoryLineData?.coreDisposition ?? ''}
                                        onChange={(e) =>
                                            updateInventoryExchangeData(e.target.value || null, 'coreDisposition')
                                        }
                                    >
                                        <option value=''>--Select--</option>
                                        {Object.values(CoreDispositionTypes).map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className='flex flex-col justify-center items-start w-full'>
                                    <RequiredFieldText text='Stock Disposition' inventoryLineData={inventoryLineData} />
                                    <select
                                        className='w-full p-3 rounded-md border bg-white'
                                        value={inventoryLineData?.stockDisposition ?? ''}
                                        onChange={(e) =>
                                            updateInventoryExchangeData(e.target.value || null, 'stockDisposition')
                                        }
                                    >
                                        <option value=''>--Select--</option>
                                        {Object.values(StockDispositionTypes).map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </article>
                        </section>
                    </div>
                </div>
            )}
            <tr
                className={
                    'text-center border-b last:border-none ' + (inventoryLineData.onRepair ? 'bg-red-600 bg-opacity-25' : '')
                }
            >
                <td className='py-3 max-w-[70px]'>{inventoryLineData.inventoryName}</td>
                <td>{inventoryLineData.condition}</td>
                <td>
                    <select
                        className='p-2 w-fit'
                        type='select'
                        defaultValue={inventoryLineData.repairCondition || null}
                        onChange={(e) => updateInventoryKeyValue(e.target.value, 'overrideCondition')}
                    >
                        <option label='NULL' value={null} />
                        <option label='NS' value={'NS'} />
                        <option label='NE' value={'NE'} />
                        <option label='OH' value={'OH'} />
                        <option label='SV' value={'SV'} />
                        <option label='AR' value={'AR'} />
                    </select>
                </td>
                <td>{inventoryLineData.OwnerCodeName}</td>
                <td>{inventoryLineData.trace}</td>
                <td>
                    {inventoryLineData.acquisitionCost?.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    })}
                </td>
                <td>{inventoryLineData.warehouseName}</td>
                <td>
                    {showPopup && inventoryLineData.comments && inventoryLineData.comments !== '' ? (
                        <article className='flex absolute z-50 w-[300px]' style={popupPositionStyle}>
                            <div className='min-w-[140px] bg-gray-800 text-white rounded-md px-2 py-1 whitespace-pre-wrap break-words'>
                                {inventoryLineData.comments}
                            </div>
                        </article>
                    ) : null}
                    <textarea
                        className='max-w-[110px] px-1 py-2 border rounded-md resize-none'
                        type='text'
                        value={inventoryLineData.comments}
                        id={inventoryLineData.id}
                        readOnly
                        onMouseOver={() => {
                            const rect = document.getElementById(inventoryLineData?.id).getBoundingClientRect();
                            const x = rect.left + window.scrollX - rect.width / 2;
                            const y = rect.bottom + window.scrollY + 4;

                            setPopupPositionStyle({ left: x, top: y });
                            setShowPopup(true);
                        }}
                        onMouseLeave={() => {
                            setShowPopup(false);
                        }}
                        onDoubleClick={() =>
                            setCommentData({
                                quoteLineId: inventoryLineData?.inventoryId,
                                comments: inventoryLineData?.comments,
                                open: true,
                                readonly: true,
                            })
                        }
                        rows={1}
                    />
                </td>
                <td>{inventoryLineData.tags}</td>
                <td>{inventoryLineData.tagDate}</td>
                <td className='max-w-[140px]'>{inventoryLineData.tagAgency}</td>
                <td>
                    {inventoryLineData.pictures ? (
                        <div className='flex flex-row justify-center items-center w-full'>
                            <a href={inventoryLineData.pictures} target='_blank'>
                                <PaperClipIcon className='w-8 text-gray-600' />
                            </a>
                            <input
                                type='checkbox'
                                className='scale-125'
                                defaultChecked={true}
                                onChange={(e) =>
                                    updatePersistedTraceSelection(e, {
                                        name: TraceTypes.TracePictures,
                                        link: inventoryLineData.pictures,
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </td>
                <td>
                    {inventoryLineData.partRecord ? (
                        <div className='flex flex-row justify-center items-center w-full'>
                            <a href={inventoryLineData.partRecord} target='_blank'>
                                <PaperClipIcon className='w-8 text-gray-600' />
                            </a>
                            <input
                                type='checkbox'
                                className='scale-125'
                                defaultChecked={true}
                                onChange={(e) =>
                                    updatePersistedTraceSelection(e, {
                                        name: TraceTypes.TracePartRecord,
                                        link: inventoryLineData.partRecord,
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </td>
                <td>
                    {inventoryLineData.tagsLink ? (
                        <div className='flex flex-row justify-center items-center w-full'>
                            <a href={inventoryLineData.tagsLink} target='_blank'>
                                <PaperClipIcon className='w-8 text-gray-600' />
                            </a>
                            <input
                                type='checkbox'
                                className='scale-125'
                                defaultChecked={true}
                                onChange={(e) =>
                                    updatePersistedTraceSelection(e, {
                                        name: TraceTypes.TraceTag,
                                        link: inventoryLineData.tagsLink,
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </td>
                <td>
                    {inventoryLineData.owner ? (
                        <div className='flex flex-row justify-center items-center w-full'>
                            <a href={inventoryLineData.owner} target='_blank'>
                                <PaperClipIcon className='w-8 text-gray-600' />
                            </a>
                            <input
                                type='checkbox'
                                className='scale-125'
                                defaultChecked={true}
                                onChange={(e) =>
                                    updatePersistedTraceSelection(e, {
                                        name: TraceTypes.TraceOwnerCode,
                                        link: inventoryLineData.owner,
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </td>
                <td>
                    {inventoryLineData.oneClick && inventoryLineData.oneClick !== 'No Binder Available' ? (
                        <div className='flex flex-row justify-center items-center w-full'>
                            <a href={inventoryLineData.oneClick} target='_blank'>
                                <PaperClipIcon className='w-8 text-gray-600' />
                            </a>
                            <input
                                type='checkbox'
                                className='scale-125'
                                defaultChecked={true}
                                onChange={(e) =>
                                    updatePersistedTraceSelection(e, {
                                        name: TraceTypes.TraceOneClick,
                                        link: inventoryLineData.oneClick,
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </td>
                <td>
                    <input
                        className='h-fit py-1 w-[50px] rounded-md border px-1'
                        type='number'
                        defaultValue={0}
                        onChange={(e) => updateInventoryKeyValue(e.target.value, 'leadTime')}
                    />
                </td>
                <td>
                    {showTypePopup &&
                    hasPortalTypeSelected ? (
                        <article
                            className='flex fixed z-50 w-[300px] -translate-x-1/2 pointer-events-none'
                            style={popupPositionStyle}
                        >
                            <div className='min-w-[140px] bg-gray-800 text-white rounded-md px-2 py-1 whitespace-pre-wrap break-words text-left'>
                                Portal Quotes cannot be Exchange.
                            </div>
                        </article>
                    ) : null}
                    <div
                        className='flex flex-row justify-center items-center gap-1 w-full'
                        onMouseEnter={(e) => {
                            if (!hasPortalTypeSelected) return;
                            const rect = e.currentTarget.getBoundingClientRect();
                            setPopupPositionStyle({ left: rect.left + rect.width / 2, top: rect.bottom + 4 });
                            setShowTypePopup(true);
                        }}
                        onMouseLeave={() => setShowTypePopup(false)}
                    >
                        <button
                            className='px-2 py-1 text-white rounded-md border bg-blue-primary disabled:cursor-default'
                            onClick={() => setShowPriceTypeModal(true)}
                            disabled={hasPortalTypeSelected} // Don't allow the user to change the type if a portal quote line is selected.
                        >
                            {inventoryLineData.type ?? 'Outright'}
                        </button>
                    </div>
                </td>
                <td>
                    <input
                        className='h-fit py-1 w-[110px] rounded-md border px-1'
                        type='number'
                        value={inventoryLineData.outrightPrice}
                        onChange={(e) => updateInventoryOutrightPrice(parseFloat(e.target.value))}
                    />
                </td>
                <td>
                    <input
                        className='h-fit py-1 w-[70px] rounded-md border px-1'
                        type='number'
                        value={inventoryLineData.quantity}
                        onChange={(e) => updateInventoryQuantity(parseFloat(e.target.value))}
                    />
                </td>
                <td className=''>
                    {inventoryLineData?.stockCheckStatus === STOCK_CHECK_STATUSES.IN_PROCESS ? (
                        <div className='px-2 py-1 mx-auto text-black rounded-md border w-fit'>Requested</div>
                    ) : inventoryLineData?.stockCheckStatus === STOCK_CHECK_STATUSES.COMPLETED ? (
                        <div className='px-2 py-1 mx-auto text-black rounded-md border w-fit'>Completed</div>
                    ) : (
                        <button
                            disabled={inventoryLineData?.stockCheckStatus !== null}
                            onClick={() => setStockCheckData({ inventoryId: inventoryLineData.id })}
                            className='px-2 py-1 mx-auto text-white rounded-md border bg-blue-primary'
                        >
                            Request
                        </button>
                    )}
                </td>
                <td>
                    <input
                        className='w-8 scale-125'
                        type='checkbox'
                        disabled={disableSelectedCheckbox()}
                        checked={selectedInventory?.some((inv) => inv === inventoryLineData.inventoryName)}
                        onChange={(e) => {
                            updateSelectedInventory(e, inventoryLineData.inventoryName, inventoryLineData.quantity);
                        }}
                    />
                </td>
            </tr>
        </>
    );
}

function RequiredFieldText({ text, inventoryLineData }) {
    return (
        <h1 className='text-md font-bold mb-2 w-fit'>
            {inventoryLineData.type === 'Exchange' ? (
                <span className='text-red-500'>* </span>
            ) : (
                ''
            )}
            {text}
        </h1>
    );
}
